import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { flowRight } from "lodash";
import Axios from "axios";
import { useTranslation } from "react-i18next";
import {
    toaster,
    Message,
    Container,
    Content,
    Header,
    Heading,
    Footer as RsuiteFooter,
    FormInstance,
} from "rsuite";
import { TypeAttributes } from "rsuite/esm/internals/types";
import { ToastContainerProps } from "rsuite/esm/toaster/ToastContainer";

import constants from "utils/constants";
import i18n from "utils/i18n";

import withLoader, { WithLoaderProps } from "hocs/withLoader/withLoader";
import withError, { WithErrorProps } from "hocs/withError/withError";

import useRefreshPage from "hooks/useRefreshPage";
import useRequestCancellation from "hooks/useRequestCancellation";
import useInactiveEnvironment from "hooks/useInactiveEnvironment";
import useInactiveTenant from "hooks/useInactiveTenant";

import { getErrorMessage } from "api/defaults";
import {
    loadCultureSettings,
    loadCurrencies,
    loadSupportedCultures,
    loadCultures,
    updateCultureSettings,
} from "api/environments/tenants/culture-settings-api";

import { AppDispatch, RootState } from "store/store";
import { getActiveEnvironmentId } from "store/environments/environment-selectors";
import { getTenantId } from "store/environments/tenants/tenants-selectors";
import { fetchTenants } from "store/environments/tenants/tenants-thunks";
import { setIsUnsavedChangeAvailable } from "store/common/unsaved-data/unsaved-data-actions";
import { fetchEnvironments } from "store/environments/environment-thunks";

import {
    CultureSettings,
    Currency,
} from "store/environments/tenants/culture-settings/culture-settings-types";

import Footer from "components/Common/Footer/Footer";
import Form from "./Form/Form";

import "./RegionalSettings.less";

const useAppDispatch = () => useDispatch<AppDispatch>();

interface Props extends WithErrorProps, WithLoaderProps {
    title?: string;
}

const defaultReginalSettings: CultureSettings = {
    culture: null,
    numberAndDateCulture: null,
    preciseScale: null,
    quantityPreciseScale: null,
    defaultCurrency: null,
};

const RegionalSettings: React.FC<Props> = ({
    title = i18n.t("tenantConfig.cultureSettingsPage.header"),
    showLoader,
    showError,
}: Props) => {
    const { t } = useTranslation();
    const cancelTokenSource = useRequestCancellation();

    const formRef = useRef<FormInstance<Record<string, any>> | null>(null);

    const params: Record<string, string> = useParams<{
        environmentId: string;
        tenantId: string;
    }>();
    const { environmentId, tenantId } = params;

    const dispatch = useAppDispatch();

    const activeEnvironmentId = useSelector((state: RootState): string =>
        getActiveEnvironmentId(state)
    );

    const activeTenantId = useSelector((state: RootState): string =>
        getTenantId(state)
    );

    const [dataFetched, setDataFetched] = useState<boolean>(false);

    const [cultureSettings, setCultureSettings] = useState<CultureSettings>(
        defaultReginalSettings
    );
    const [currencies, setCurrencies] = useState<Currency[]>([]);

    const [supportedCultures, setSupportedCultures] = useState<string[]>([]);
    const [cultures, setCultures] = useState<string[]>([]);

    const tostOptions: ToastContainerProps = {
        duration: constants.alertDurationMsec,
    };

    const fetchData = () => {
        showLoader?.(true);
        showError?.("");

        if (formRef && formRef.current) {
            formRef.current.cleanErrors();
        }

        Promise.all([
            fetchCultureSettings(),
            fetchCurrencies(),
            fetchSupportedCultures(),
            fetchCultures(),
        ])
            .then((value) => {
                if (value.some((v) => typeof v == "string")) {
                    const error = value
                        .filter((v) => typeof v == "string")
                        .join(". ");
                    showError?.(error);
                }
            })
            .finally(() => {
                showLoader?.(false);
                setDataFetched(true);
            });
    };

    const fetchCultureSettings = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadCultureSettings(
                activeEnvironmentId,
                activeTenantId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setCultureSettings(payload);

                return null;
            }

            if (status === constants.statusCode.NotFound) {
                setCultureSettings(defaultReginalSettings);

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!Axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchCurrencies = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadCurrencies(
                activeEnvironmentId,
                activeTenantId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setCurrencies(payload);

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!Axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchSupportedCultures = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadSupportedCultures(
                activeEnvironmentId,
                activeTenantId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setSupportedCultures(payload);

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!Axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchCultures = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadCultures(
                activeEnvironmentId,
                activeTenantId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setCultures(payload);

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!Axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const submitCultureSettings = async () => {
        try {
            const [payload, status] = await updateCultureSettings(
                activeEnvironmentId,
                activeTenantId,
                cultureSettings!
            );
            if (
                status === constants.statusCode.OK ||
                status === constants.statusCode.NoContent
            ) {
                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!Axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const handleSubmit = () => {
        if (!formRef.current?.check()) {
            return;
        }

        showLoader?.(true);
        Promise.all([submitCultureSettings()]).then((value) => {
            showLoader?.(false);
            dispatch(setIsUnsavedChangeAvailable(false));

            let message: string;
            let type: TypeAttributes.Status;

            if (value.some((v) => typeof v == "string")) {
                const error = value
                    .filter((v) => typeof v == "string")
                    .join(". ");

                type = "error";
                message = error;
            } else {
                type = "success";
                message = t("tenantConfig.common.successfulUpdateAlertMessage");
            }

            toaster.push(
                <Message showIcon type={type}>
                    {message}
                </Message>,
                tostOptions
            );
        });
    };

    const handleDataChange = (data: Record<string, any>) => {
        setCultureSettings(data as CultureSettings);

        dispatch(setIsUnsavedChangeAvailable(true));
    };

    useEffect(() => {
        if (activeEnvironmentId && activeTenantId) {
            fetchData();
        }
    }, [activeEnvironmentId, activeTenantId]);

    useEffect(() => {
        if (!activeEnvironmentId) {
            dispatch(fetchEnvironments(environmentId));
        }
    }, [activeEnvironmentId, dispatch]);

    useEffect(() => {
        if (!activeTenantId && activeEnvironmentId) {
            dispatch(fetchTenants(activeEnvironmentId, tenantId));
        }
    }, [activeEnvironmentId, dispatch]);

    useRefreshPage(() => {
        if (activeEnvironmentId && activeTenantId) {
            fetchData();
        }
    });

    useInactiveEnvironment();
    useInactiveTenant();

    return (
        dataFetched && (
            <Container className="regional_settings__container">
                <Header className="regional_settings__header">
                    <Heading level={1}>{title}</Heading>
                </Header>

                <Content>
                    <Form
                        formRef={formRef}
                        cultureSettings={cultureSettings}
                        currencies={currencies}
                        supportedCultures={supportedCultures}
                        cultures={cultures}
                        onChange={(formValue) => handleDataChange(formValue)}
                    />
                </Content>

                <RsuiteFooter>
                    <Footer onSubmit={() => handleSubmit()} />
                </RsuiteFooter>
            </Container>
        )
    );
};

const Component = flowRight(withLoader, withError)(RegionalSettings);

export default Component;
